import { Text } from '@afterpaytouch/core'
import React from 'react'
import { CardBrand, CashAppPayDetail, PaymentMethodType, PaymentType } from '@afterpay/types'
import { useTranslation } from '../../utils/convergence'
import styles from './style.module.css'
import { EnrichedBankAccountDetails, EnrichedCardDetails, Features } from '../../model'
import { useIsAfterpayFinanceFeeEligible, useSelectedPaymentType } from '../../state'
import { useFlag } from '@afterpaytouch/feature'

interface PaymentMethodErrorMessageProps {
  paymentMethodType: PaymentMethodType
  card?: EnrichedCardDetails
  account?: EnrichedBankAccountDetails
  cashAppPay?: CashAppPayDetail
}

const cardExpiryLimitInMonths = 2

// This is only for inline payment method list design.
export const PaymentMethodErrorMessage: React.FC<PaymentMethodErrorMessageProps> = ({ paymentMethodType, card, account, cashAppPay }) => {
  const { t } = useTranslation()
  const isAff = useIsAfterpayFinanceFeeEligible()

  const selectedPaymentType = useSelectedPaymentType() ?? PaymentType.PBI
  const cashPayMonthlyEnabled = useFlag(Features.CONVERGENCE_CASH_PAY_MONTHLY_ENABLED)

  const getErrorMessage = (): string | undefined => {
    if (paymentMethodType === PaymentMethodType.ACH_BANK_ACCOUNT && account?.error) {
      return t(`paymentMethod:bankAccountErrors.generic`)
    }

    if (paymentMethodType === PaymentMethodType.CASH_APP_PAY && cashAppPay != null) {
      const isConsumerLending = selectedPaymentType === PaymentType.PCL
      const isInactive = cashAppPay.status !== 'ACTIVE'
      const hasError = (isConsumerLending && !cashPayMonthlyEnabled) || isInactive

      if (hasError) {
        return t(`paymentMethod:errors.cashAppPayNotAllowed`, {
          context: 'inline',
        })
      }
    }

    if (paymentMethodType === PaymentMethodType.CREDIT_CARD && card?.error) {
      if (card.cardBrand === CardBrand.AMEX && card.error === 'invalidBrand') {
        return t('paymentMethod:errors.amexUnsupported', { context: 'inline' })
      }

      return t(`paymentMethod:errors.${card.error}`, {
        selectedCardBrand: card.cardBrand ?? '',
        months: cardExpiryLimitInMonths,
      })
    }

    if (isAff) {
      if (paymentMethodType === PaymentMethodType.CREDIT_CARD && card?.creditCardType !== 'DEBIT') {
        return t('paymentMethod:errors.ineligibleCreditCard')
      }

      if (paymentMethodType === PaymentMethodType.ACH_BANK_ACCOUNT) {
        return t('paymentMethod:errors.ineligibleBankAccount')
      }
    }
  }

  const errorMessage = getErrorMessage()

  if (!errorMessage) {
    return null
  }

  return (
    <div className={styles.radioButtonErrorContainer}>
      <Text size={'XS'} color='Subtle'>
        {errorMessage}
      </Text>
    </div>
  )
}
